<script setup>
/**
 * 来客数予測分析 / VISITOR FORECAST 詳細ページ (DEV-14331)
 *
 * Figma node 1504-1156 の単一映画（国宝）向け来客数予測ダッシュボード。
 * 静的モックデータ（ローカル fixture）でレンダリングする UI/UX 専用ビュー。
 * API 連携は後続チケットで対応する。
 *
 * チャートのライフサイクルは review-detail.vue と同じ規律で扱い、
 * chart-nav-guard.ts の dispose ヘルパーを再利用して
 * ルート離脱後に Chart.js の Animator が残らないようにする。
 */
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import {
  destroyAllChartsUnderSelector,
  markClientV2DetailChartsDisposing,
} from "@/views/dp/client-v2/chart-nav-guard";

Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Filler
);

const router = useRouter();

// ─── Design tokens (Figma) ────────────────────────────────────
const COLOR_ORANGE = "#F97316";
const COLOR_GREEN = "#10B981";
const COLOR_GRAY = "#9CA3AF";
/** テーマ連動の二次テキスト（html.dark で --text-secondary が変わる） */
const COLOR_TEXT = "var(--text-secondary)";
// Figma の 15 ストップは #F9DA49 → #F97316 の線形補間なので 2 ストップに簡約
// 主色はブランドトークン（light 赤 / dark 黄）に連動させる
const KPI_ACCENT_GRADIENT = "linear-gradient(90deg, var(--brand-color, #F9DA49) 0%, #F97316 100%)";

const readCssVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
};

/** Chart.js は CSS var を直接解釈しないため、構築時に解決する */
const getChartAxisColors = () => {
  const isDark = document.documentElement.classList.contains("dark");
  return {
    tick: readCssVar("--text-secondary", "#4A4A4A"),
    title: readCssVar("--text-tertiary", "#808080"),
    border: readCssVar("--text-secondary", "#4A4A4A"),
    grid: isDark ? "rgba(255, 255, 255, 0.08)" : "#E5E5E5",
  };
};

// ─── 静的モックデータ（Figma サンプル値）────────────────────────
const header = {
  movie: "国宝",
  lastUpdated: "2026年1月3日 06:00",
  aggPeriod: "2025/12/20 ~ 2026/01/02（実績）",
  forecastPeriod: "2026/01/03 ~ 01/05（3日先）",
  model: "AI v2.4",
  status: "稼働中",
};

// Figma node 1617-166 KPI row: 上端アクセントバー / 右上アイコン / 値・サブ行の配色
const kpis = [
  {
    label: "累計来客数",
    value: "48,470",
    valueColor: "var(--text-primary)",
    icon: "fas fa-users",
    iconColor: COLOR_TEXT,
    accent: KPI_ACCENT_GRADIENT,
    sub: "実績値（確定）",
    subColor: COLOR_TEXT,
    trendIcon: null,
  },
  {
    label: "本日予測",
    value: "3,245",
    valueColor: COLOR_GREEN,
    icon: "far fa-calendar",
    iconColor: COLOR_GREEN,
    accent: COLOR_GREEN,
    sub: "+7.4% (前日比)",
    subColor: COLOR_GREEN,
    trendIcon: "fas fa-arrow-trend-up",
    trendColor: COLOR_GREEN,
  },
  {
    label: "明日予測",
    value: "4,680",
    valueColor: COLOR_GRAY,
    icon: "far fa-calendar",
    iconColor: COLOR_GRAY,
    accent: COLOR_GRAY,
    sub: "+44.2% (本日比)",
    subColor: COLOR_TEXT,
    trendIcon: "fas fa-arrow-trend-up",
    trendColor: COLOR_GREEN,
  },
  {
    label: "3日後予測",
    value: "4,420",
    valueColor: COLOR_GRAY,
    icon: "far fa-calendar",
    iconColor: COLOR_GRAY,
    accent: COLOR_GRAY,
    sub: "-5.6% (明日比)",
    subColor: COLOR_TEXT,
    trendIcon: "fas fa-arrow-trend-down",
    trendColor: COLOR_ORANGE,
  },
  {
    label: "現在充足率",
    value: "20.3%",
    valueColor: COLOR_ORANGE,
    icon: "far fa-eye",
    iconColor: COLOR_ORANGE,
    accent: KPI_ACCENT_GRADIENT,
    sub: "総座席数: 16,000",
    subColor: COLOR_TEXT,
    trendIcon: null,
  },
];

/**
 * 日別データ：13 実績（12/20–01/02）+ 3 予測（01/03–01/05）。
 * 合成チャートでは 17 ティックの X 軸としてレンダリングする
 * （実績の点が連続し、予測の最後 3 点が破線で繋がる）。
 */
const daily = [
  { date: "12/20", weekday: "金", visitors: 2850, forecastRange: null, totalSeats: 15200, occupancy: 18.8, screenings: 85, avgPerScreening: 34, dod: null, kind: "実績", holiday: false },
  { date: "12/21", weekday: "土", visitors: 4520, forecastRange: null, totalSeats: 18600, occupancy: 24.3, screenings: 102, avgPerScreening: 44, dod: 58.6, kind: "実績", holiday: false },
  { date: "12/22", weekday: "日", visitors: 4180, forecastRange: null, totalSeats: 18600, occupancy: 22.5, screenings: 102, avgPerScreening: 41, dod: -7.5, kind: "実績", holiday: false },
  { date: "12/23", weekday: "月", visitors: 2420, forecastRange: null, totalSeats: 14800, occupancy: 16.4, screenings: 82, avgPerScreening: 30, dod: -42.1, kind: "実績", holiday: false },
  { date: "12/24", weekday: "火", visitors: 2680, forecastRange: null, totalSeats: 15200, occupancy: 17.6, screenings: 85, avgPerScreening: 32, dod: 10.7, kind: "実績", holiday: false },
  { date: "12/25", weekday: "水", visitors: 2950, forecastRange: null, totalSeats: 15200, occupancy: 19.4, screenings: 85, avgPerScreening: 35, dod: 10.1, kind: "実績", holiday: false },
  { date: "12/26", weekday: "木", visitors: 2820, forecastRange: null, totalSeats: 15200, occupancy: 18.6, screenings: 85, avgPerScreening: 33, dod: -4.4, kind: "実績", holiday: false },
  { date: "12/27", weekday: "金", visitors: 3050, forecastRange: null, totalSeats: 15600, occupancy: 19.6, screenings: 88, avgPerScreening: 35, dod: 8.2, kind: "実績", holiday: false },
  { date: "12/28", weekday: "土", visitors: 4850, forecastRange: null, totalSeats: 19200, occupancy: 25.3, screenings: 108, avgPerScreening: 45, dod: 59.0, kind: "実績", holiday: false },
  { date: "12/29", weekday: "日", visitors: 4620, forecastRange: null, totalSeats: 19200, occupancy: 24.1, screenings: 108, avgPerScreening: 43, dod: -4.7, kind: "実績", holiday: false },
  { date: "12/30", weekday: "月", visitors: 2680, forecastRange: null, totalSeats: 15600, occupancy: 17.2, screenings: 88, avgPerScreening: 30, dod: -42.0, kind: "実績", holiday: false },
  { date: "12/31", weekday: "火", visitors: 2850, forecastRange: null, totalSeats: 15600, occupancy: 18.3, screenings: 88, avgPerScreening: 32, dod: 6.3, kind: "実績", holiday: false },
  { date: "01/01", weekday: "水", visitors: 3180, forecastRange: null, totalSeats: 16200, occupancy: 19.6, screenings: 92, avgPerScreening: 35, dod: 11.6, kind: "実績", holiday: true },
  { date: "01/02", weekday: "木", visitors: 3020, forecastRange: null, totalSeats: 15800, occupancy: 19.1, screenings: 89, avgPerScreening: 34, dod: -5.0, kind: "実績", holiday: false },
  { date: "01/03", weekday: "金", visitors: 3245, forecastRange: "2,980 ~ 3,520", totalSeats: 16000, occupancy: 20.3, screenings: 90, avgPerScreening: 36, dod: 7.5, kind: "予測", holiday: false },
  { date: "01/04", weekday: "土", visitors: 4680, forecastRange: "4,320 ~ 5,050", totalSeats: 19500, occupancy: 24, screenings: 110, avgPerScreening: 43, dod: 44.2, kind: "予測", holiday: false },
  { date: "01/05", weekday: "日", visitors: 4420, forecastRange: "4,080 ~ 4,780", totalSeats: 19500, occupancy: 22.7, screenings: 110, avgPerScreening: 40, dod: -5.6, kind: "予測", holiday: false },
];

// AI 総合分析レポート: 短期施策アクションプラン（Figma node 1617-166）
const shortTermActions = [
  {
    title: "平日プロモーション強化",
    description: "平日限定割引や学生向けキャンペーンで充足率20%超えを目指す。特に火曜・水曜の集客強化が有効。",
  },
  {
    title: "SNS施策の展開",
    description: "口コミ評価4.3を活かしたハッシュタグキャンペーンで拡散を促進。特典付き投稿企画で新規層の取り込みを強化。",
  },
  {
    title: "地方エリア施策",
    description: "充足率が低い北海道・静岡エリアで地域限定イベントや舞台挨拶配信を実施。",
  },
];

// ─── AI 総合分析レポート: タイプライター演出 ────────────────────
// Google AI 検索風に本文を1文字ずつストリーミング表示する。
// 色付き強調が混在するためセグメント { text, em?: "bold"|"green"|"orange" } 単位で管理し、
// グローバル文字カウンタ typedTotal がブロック順（サマリー → 分析4段落 → 施策3件）に進む。
const typingBlocks = {
  summary: [
    { text: "作品「国宝」は公開から6ヶ月が経過し、" },
    { text: "好調な推移を維持", em: "green" },
    { text: "しています。直近14日間の平均来客数は" },
    { text: "3,308人/日", em: "bold" },
    { text: "、平均充足率は" },
    { text: "19.8%", em: "bold" },
    { text: "で安定しており、週末（土日）は平日比" },
    { text: "+61.4%", em: "orange" },
    { text: "の来客数増加が見られます。" },
  ],
  p1: [
    { text: "直近14日間の平均来客数は" },
    { text: "3,308人/日", em: "bold" },
    { text: "。平日は平均" },
    { text: "2,850人", em: "bold" },
    { text: "、土日は" },
    { text: "4,600人", em: "orange" },
    { text: "で、週末は平日比" },
    { text: "+61.4%", em: "orange" },
    { text: "の高い集客を示しています。" },
  ],
  p2: [
    { text: "上映回数との相関では、土日110回上映時に平均" },
    { text: "42.5人/回", em: "bold" },
    { text: "、平日85回上映時に平均" },
    { text: "33.5人/回", em: "bold" },
    { text: "の来客があり、上映回数増加が集客に直接的な影響を与えています。" },
  ],
  p3: [
    { text: "エリア別では東京が充足率" },
    { text: "23.5%", em: "bold" },
    { text: "でトップ、神奈川" },
    { text: "22.1%", em: "bold" },
    { text: "、大阪" },
    { text: "21.8%", em: "bold" },
    { text: "と続き、都市部で高いパフォーマンスを維持しています。" },
  ],
  p4: [
    { text: "劇場別ではTOHOシネマズ新宿が充足率" },
    { text: "28.5%", em: "green" },
    { text: "で最高値。一方、北海道（18.6%）や静岡（19.2%）などの地方エリアは全国平均をやや下回っており、地域別の施策強化が必要です。" },
  ],
};

// 施策リストもタイトル・説明を1ブロックずつ順番に流す
shortTermActions.forEach((a, i) => {
  typingBlocks[`a${i}t`] = [{ text: a.title }];
  typingBlocks[`a${i}d`] = [{ text: a.description }];
});

const TYPE_ORDER = ["summary", "p1", "p2", "p3", "p4", "a0t", "a0d", "a1t", "a1d", "a2t", "a2d"];
const typeBlockLen = (id) => typingBlocks[id].reduce((n, s) => n + s.text.length, 0);
const TYPE_START = {};
let typeAcc = 0;
for (const id of TYPE_ORDER) {
  TYPE_START[id] = typeAcc;
  typeAcc += typeBlockLen(id);
}
const TYPE_TOTAL = typeAcc;

/** 表示済みのグローバル文字数（0 → TYPE_TOTAL まで進む） */
const typedTotal = ref(0);
let typingTimer = null;
let typingDelayTimer = null;

/** ブロック内セグメントの現在の表示文字列 */
const segText = (id, segIdx) => {
  const segs = typingBlocks[id];
  let before = TYPE_START[id];
  for (let k = 0; k < segIdx; k += 1) before += segs[k].text.length;
  const n = typedTotal.value - before;
  if (n <= 0) return "";
  const t = segs[segIdx].text;
  return n >= t.length ? t : t.slice(0, n);
};

/** カーソル（点滅キャレット）を出すブロックか */
const isTypingNow = (id) =>
  typedTotal.value < TYPE_TOTAL &&
  typedTotal.value >= TYPE_START[id] &&
  typedTotal.value < TYPE_START[id] + typeBlockLen(id);

/** タイピングがそのブロックに到達したか（施策行の出現制御に使用） */
const blockStarted = (id) => typedTotal.value >= TYPE_START[id];

const emClass = (em) => (em === "green" ? "vf-em-green" : em === "orange" ? "vf-em-orange" : "");

const startTyping = () => {
  // タイプライター演出（1文字ずつ表示）は廃止。
  // 全文を一括表示し、出現演出は CSS アニメーション（vf-report-reveal）で行う。
  // typedTotal を即座に最大値にすることで segText は全文を返し、isTypingNow は常に false（キャレット非表示）になる。
  typedTotal.value = TYPE_TOTAL;
};

const stopTyping = () => {
  if (typingDelayTimer) {
    clearTimeout(typingDelayTimer);
    typingDelayTimer = null;
  }
  if (typingTimer) {
    clearInterval(typingTimer);
    typingTimer = null;
  }
};

// 予測根拠・影響要因（Figma node 1617-166: 6タイル横並び、score はプログレスバーの%を兼ねる）
const aiFactors = [
  { score: 92, title: "充足率が高水準維持", description: "直近1週間の充足率が20%超を維持しているため、今後も高水準見込み" },
  { score: 85, title: "週末要因", description: "土日の来客数は平日比1.6倍の傾向があり、週末は増加予測" },
  { score: 78, title: "上映回数増加", description: "土日は上映回数が108回に増加するため、来客受入れ可能数が増加" },
  { score: 65, title: "公開からの日数", description: "公開から6ヶ月経過し、徐々に減少傾向だが、まだ一定の集客力を維持" },
  { score: 58, title: "口コミ評価維持", description: "評価4.3を維持し、新規来客の流入が継続" },
  { score: 58, title: "天気要因", description: "評価4.3を維持し、新規来客の流入が継続" },
];

// tier: green=好調ハイライト / plain=中立 / red=下降警告 / orange=注意 (Figma node 1617-166)
const areas = [
  { name: "東京", occupancy: 23.5, seats: 54200, actual: "12,850", forecast: "14,200", deltaPct: "+10.5%", tier: "green" },
  { name: "大阪", occupancy: 21.8, seats: 38600, actual: "8,420", forecast: "9,150", deltaPct: "+8.7%", tier: "plain" },
  { name: "愛知", occupancy: 19.2, seats: 27500, actual: "5,280", forecast: "5,820", deltaPct: "+10.2%", tier: "plain" },
  { name: "福岡", occupancy: 20.5, seats: 19200, actual: "3,950", forecast: "4,280", deltaPct: "+8.4%", tier: "plain" },
  { name: "北海道", occupancy: 18.6, seats: 16800, actual: "3,120", forecast: "3,380", deltaPct: "+8.3%", tier: "plain" },
  { name: "神奈川", occupancy: 22.1, seats: 30900, actual: "6,850", forecast: "6,420", deltaPct: "-8.3%", tier: "red" },
  { name: "埼玉", occupancy: 20.8, seats: 20600, actual: "4,280", forecast: "4,650", deltaPct: "+8.6%", tier: "plain" },
  { name: "千葉", occupancy: 19.8, seats: 19300, actual: "3,820", forecast: "4,150", deltaPct: "+8.6%", tier: "plain" },
];

const theatres = [
  { rank: 1, name: "TOHOシネマズ新宿", occupancy: 28.5, actual: "1,850", forecast: "2,020", tier: "green" },
  { rank: 2, name: "TOHOシネマズ六本木", occupancy: 26.8, actual: "1,620", forecast: "1,780", tier: "green" },
  { rank: 3, name: "ユナイテッドシネマ豊洲", occupancy: 24.2, actual: "1,420", forecast: "1,550", tier: "plain" },
  { rank: 4, name: "イオンシネマ幕張新都心", occupancy: 22.5, actual: "1,280", forecast: "1,390", tier: "plain" },
  { rank: 5, name: "109シネマズ二子玉川", occupancy: 20.8, actual: "980", forecast: "1,050", tier: "plain" },
  { rank: 6, name: "グランドシネマサンシャイン", occupancy: 19.2, actual: "850", forecast: "720", tier: "red" },
  { rank: 7, name: "T・ジョイPRINCE品川", occupancy: 18.5, actual: "720", forecast: "780", tier: "orange" },
];

// ─── 合成チャート ──────────────────────────────────────────────
const vfChartRef = ref(null);
const vfChartInstance = ref(null);
/** false 时从 DOM 移除 canvas，避免离开路由后 Animator 仍在 draw */
const vfChartMounted = ref(true);
let vfChartDisposePromise = null;

const waitFrames = (count = 3) =>
  new Promise((resolve) => {
    let left = count;
    const step = () => {
      if (left <= 0) {
        resolve();
        return;
      }
      left -= 1;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });

/** 直近に構築したチャートがモバイルレイアウトか（ブレークポイント跨ぎ検知用） */
let vfChartBuiltMobile = null;

// ─── カスタム HTML ツールチップ（Figma: 296px 白カード + AI 分析レポート）───
/** "12/25" → "2025/12/25"（12月=2025年 / 1月=2026年） */
const tooltipFullDate = (d) => `${d.date.startsWith("01/") ? "2026" : "2025"}/${d.date}`;

/** ツールチップ用の日別 AI コメント（モック生成。API 連携時に置き換え） */
const tooltipAiNote = (d) => {
  const forecast = d.kind === "予測" ? `AI予測値は${d.visitors.toLocaleString()}人（範囲: ${d.forecastRange}）。` : "";
  const dod =
    d.dod == null
      ? ""
      : d.dod >= 0
        ? `前日比+${d.dod.toFixed(1)}%と増加しており、`
        : `前日比${d.dod.toFixed(1)}%と減少していますが、`;
  const occupancy = `充足率は${d.occupancy}%で${d.occupancy >= 20 ? "目標の20%を上回っています" : "全体平均並みの水準です"}。`;
  const trend =
    d.weekday === "土" || d.weekday === "日"
      ? "週末効果により高い集客水準です。"
      : "平日水準で安定した推移です。";
  return `${forecast}${dod}${occupancy}${trend}`;
};

/** Chart.js external tooltip — canvas 描画では再現できない drop-shadow / グラデーションバッジを HTML で描く */
const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const wrap = chart.canvas?.parentNode;
  if (!wrap) return;
  let el = wrap.querySelector(".vf-chart-tooltip");
  if (!el) {
    el = document.createElement("div");
    el.className = "vf-chart-tooltip";
    wrap.appendChild(el);
  }
  const idx = tooltip.dataPoints?.[0]?.dataIndex ?? -1;
  const d = daily[idx];
  if (tooltip.opacity === 0 || !d) {
    el.style.opacity = "0";
    return;
  }
  // 実績日は緑、予測日はグレーで来客数を表示（チャート系列色のトーンに対応）
  const visitorsLine =
    d.kind === "予測"
      ? `<div class="vf-tt-line--forecast">予測来客数: ${d.visitors.toLocaleString()}人</div>`
      : `<div class="vf-tt-line--actual">実績来客数: ${d.visitors.toLocaleString()}人</div>`;
  // 値はすべてローカル fixture 由来（ユーザー入力なし）のため innerHTML で安全
  el.innerHTML = `
    <div class="vf-tt-date">${tooltipFullDate(d)}</div>
    <div class="vf-tt-values">
      ${visitorsLine}
      <div class="vf-tt-line--occupancy">充足率: ${d.occupancy}%</div>
    </div>
    <div class="vf-tt-ai">
      <div class="vf-tt-ai-head">
        <span class="vf-tt-ai-badge">AI</span>
        <span class="vf-tt-ai-title">分析レポート</span>
      </div>
      <div class="vf-tt-ai-body">${tooltipAiNote(d)}</div>
    </div>
  `;
  el.style.opacity = "1";
  // カーソル横に表示し、チャート領域からはみ出さないようクランプ
  const ttW = el.offsetWidth;
  const ttH = el.offsetHeight;
  let left = tooltip.caretX + 12;
  if (left + ttW > wrap.clientWidth - 4) left = tooltip.caretX - ttW - 12;
  const top = Math.max(4, Math.min(tooltip.caretY - ttH / 2, wrap.clientHeight - ttH - 4));
  el.style.left = `${Math.max(4, left)}px`;
  el.style.top = `${top}px`;
};

const buildChart = () => {
  const canvas = vfChartRef.value;
  if (!canvas || !vfChartMounted.value) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // モバイル: 目盛を縮小し、軸タイトルを隠してプロット領域を確保、X ラベルは斜めに
  const isMobile = window.innerWidth <= 768;
  vfChartBuiltMobile = isMobile;
  const tickFont = { size: isMobile ? 10 : 12 };
  const axis = getChartAxisColors();

  const labels = daily.map((d) => d.date);
  const FORECAST_START = daily.findIndex((d) => d.kind === "予測");

  // 実績：予測区間は null（実線は実績だけを繋ぐ）
  const actualSeries = daily.map((d, i) =>
    i <= (FORECAST_START === -1 ? daily.length - 1 : FORECAST_START - 1) ? d.visitors : null
  );
  // 予測：直前の実績点から繋がるよう、実績の最終点を含めて破線を描く
  const forecastSeries = daily.map((d, i) =>
    FORECAST_START !== -1 && i >= FORECAST_START - 1 ? d.visitors : null
  );
  const occupancySeries = daily.map((d) => d.occupancy);

  vfChartInstance.value = new Chart(ctx, {
    data: {
      labels,
      datasets: [
        {
          // Figma: #0066FF 50% / 2px、白抜きドット
          type: "line",
          label: "充足率",
          data: occupancySeries,
          yAxisID: "yRate",
          borderColor: "rgba(0, 102, 255, 0.5)",
          borderWidth: 2,
          fill: false,
          tension: 0.35,
          pointRadius: isMobile ? 2.5 : 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#FFFFFF",
          pointBorderColor: "rgba(0, 102, 255, 0.5)",
          pointBorderWidth: 2,
          order: 3,
        },
        {
          // Figma: #10B981 3px + 下方向グラデーション面 (0.18 → 0)
          type: "line",
          label: "実績",
          data: actualSeries,
          yAxisID: "yVisitors",
          borderColor: COLOR_GREEN,
          borderWidth: 3,
          fill: true,
          backgroundColor: (context) => {
            const { ctx: c, chartArea } = context.chart;
            if (!chartArea) return "rgba(16, 185, 129, 0.18)";
            const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            g.addColorStop(0.05, "rgba(16, 185, 129, 0.18)");
            g.addColorStop(0.95, "rgba(16, 185, 129, 0)");
            return g;
          },
          tension: 0.3,
          spanGaps: false,
          pointRadius: 0,
          pointHoverRadius: 6,
          order: 1,
        },
        {
          // Figma: #9CA3AF 2px dashed
          type: "line",
          label: "予測",
          data: forecastSeries,
          yAxisID: "yVisitors",
          borderColor: COLOR_GRAY,
          borderWidth: 2,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
          spanGaps: false,
          pointRadius: 0,
          pointHoverRadius: 6,
          order: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          // Figma 仕様の白カード + AI 分析レポートを HTML で描画（canvas 内蔵 tooltip は無効化）
          enabled: false,
          external: externalTooltipHandler,
        },
      },
      scales: {
        // 軸色は CSS トークン経由（dark: 白半透明グリッド + --text-secondary 目盛）
        yVisitors: {
          type: "linear",
          position: "left",
          min: 0,
          max: 6000,
          grid: { color: axis.grid },
          border: { color: axis.border, dash: [3, 3] },
          title: { display: !isMobile, text: "来客数（人）", color: axis.title, font: { size: 12 } },
          ticks: { color: axis.tick, font: tickFont, stepSize: 1500 },
        },
        yRate: {
          type: "linear",
          position: "right",
          min: 0,
          max: 28,
          grid: { drawOnChartArea: false },
          border: { color: axis.border },
          title: { display: !isMobile, text: "充足率（%）", color: axis.title, font: { size: 12 } },
          ticks: { color: axis.tick, font: tickFont, stepSize: 7 },
        },
        x: {
          grid: { color: axis.grid },
          border: { color: axis.border, dash: [3, 3] },
          ticks: {
            color: axis.tick,
            font: tickFont,
            maxRotation: isMobile ? 45 : 0,
            minRotation: 0,
          },
        },
      },
    },
  });
};

const disposeChart = () => {
  if (vfChartDisposePromise) return vfChartDisposePromise;
  vfChartMounted.value = false;
  const work = (async () => {
    await nextTick();
    try {
      const chart =
        vfChartInstance.value ?? (vfChartRef.value ? Chart.getChart(vfChartRef.value) : null);
      if (chart) {
        try {
          chart.options.events = [];
        } catch {
          /* ignore */
        }
        try {
          chart.stop();
        } catch {
          /* ignore */
        }
        await waitFrames(2);
        try {
          if (chart.ctx) chart.destroy();
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }
    vfChartInstance.value = null;
    await destroyAllChartsUnderSelector(".vf-detail-page");
    await waitFrames(3);
  })();
  vfChartDisposePromise = work;
  markClientV2DetailChartsDisposing(work);
  return work;
};

/** 768px ブレークポイントを跨いだときだけチャートを作り直す（目盛・回転・軸タイトルが変わるため） */
const handleViewportChange = () => {
  if (!vfChartMounted.value || vfChartDisposePromise) return;
  const isMobile = window.innerWidth <= 768;
  if (vfChartBuiltMobile === null || vfChartBuiltMobile === isMobile) return;
  const chart = vfChartInstance.value;
  if (chart) {
    try {
      chart.destroy();
    } catch {
      /* ignore */
    }
    vfChartInstance.value = null;
  }
  buildChart();
};

onMounted(() => {
  vfChartDisposePromise = null;
  vfChartMounted.value = true;
  window.addEventListener("resize", handleViewportChange);
  // AI レポートのストリーミング表示は少し間を置いて開始（生成中の演出）
  typingDelayTimer = setTimeout(startTyping, 500);
  nextTick(() => {
    if (vfChartRef.value) buildChart();
    else nextTick(buildChart);
  });
});

onBeforeRouteLeave(async () => {
  await disposeChart();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleViewportChange);
  stopTyping();
  void disposeChart();
});

const goBack = async () => {
  await disposeChart();
  await waitFrames(2);
  if (window.history.length > 1) {
    router.back();
    return;
  }
  router.push("/dp/client-v2");
};

// ─── CSV 出力 ─────────────────────────────────────────────────
const CSV_HEADERS = [
  "日付",
  "曜日",
  "来客数",
  "総座席数",
  "充足率",
  "上映回数",
  "1回平均",
  "前日比",
  "区分",
];

const csvCell = (value) => {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const exportCsv = () => {
  try {
    const rows = daily.map((d) => [
      d.date,
      d.weekday,
      d.kind === "予測" && d.forecastRange ? `${d.visitors} (${d.forecastRange})` : String(d.visitors),
      String(d.totalSeats),
      `${d.occupancy}%`,
      String(d.screenings),
      String(d.avgPerScreening),
      d.dod == null ? "-" : `${d.dod > 0 ? "+" : ""}${d.dod.toFixed(1)}%`,
      d.kind,
    ]);
    const lines = [CSV_HEADERS, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    // UTF-8 BOM for Excel (JP) compatibility
    const blob = new Blob(["﻿" + lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `来客数予測_日別明細_${header.movie}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    // 非ブロッキング：失敗してもページは使用可能なまま
    // eslint-disable-next-line no-console
    console.error("[visitor-forecast-detail] CSV出力に失敗しました", e);
  }
};

// ─── AI チャット（右下フローティング・UI のみ）─────────────────
const aiChatInput = ref("");
const sendAiChat = () => {
  if (!aiChatInput.value.trim()) return;
  // TODO: AI エージェント API 連携は後続チケットで対応
  aiChatInput.value = "";
};

/** 予測行は #F5F5F5 で網掛け（Figma node 1617-166） */
const dailyRowClass = ({ row }) => (row.kind === "予測" ? "vf-forecast-row" : "");

const isWeekend = (weekday) => weekday === "土" || weekday === "日";

const dodClass = (dod) => {
  if (dod == null) return "";
  return dod >= 0 ? "vf-pos" : "vf-neg";
};
const dodText = (dod) => {
  if (dod == null) return "-";
  return `${dod > 0 ? "+" : ""}${dod.toFixed(1)}%`;
};
</script>

<template>
  <div class="vf-detail-page">
    <!-- 1. Header card -->
    <div class="vf-card vf-header-card">
      <div class="vf-header-top">
        <button class="vf-back-btn" @click="goBack">
          <i class="fas fa-arrow-left"></i>
          <span>戻る</span>
        </button>
        <span class="vf-header-divider" aria-hidden="true"></span>
        <div class="vf-header-titles">
          <span class="vf-eyebrow">VISITOR FORECAST</span>
          <h1 class="vf-title">来客数予測分析</h1>
        </div>
        <div class="vf-last-updated">最終更新: {{ header.lastUpdated }}</div>
      </div>
      <div class="vf-meta-row">
        <span class="vf-meta">作品名: {{ header.movie }}</span>
        <span class="vf-dot">•</span>
        <span class="vf-meta">集計期間: {{ header.aggPeriod }}</span>
        <span class="vf-dot">•</span>
        <span class="vf-meta">予測期間: {{ header.forecastPeriod }}</span>
        <span class="vf-dot">•</span>
        <span class="vf-meta vf-model">
          <span class="vf-status-dot"></span>
          予測モデル: {{ header.model }}
        </span>
      </div>
    </div>

    <!-- 2. KPI stat-card row -->
    <div class="vf-kpi-grid">
      <div v-for="kpi in kpis" :key="kpi.label" class="vf-kpi-card">
        <div class="vf-kpi-accent" :style="{ background: kpi.accent }"></div>
        <div class="vf-kpi-label-row">
          <span class="vf-kpi-label">{{ kpi.label }}</span>
          <i class="vf-kpi-icon" :class="kpi.icon" :style="{ color: kpi.iconColor }"></i>
        </div>
        <div class="vf-kpi-value" :style="{ color: kpi.valueColor }">{{ kpi.value }}</div>
        <div class="vf-kpi-sub" :style="{ color: kpi.subColor }">
          <i
            v-if="kpi.trendIcon"
            class="vf-kpi-trend-icon"
            :class="kpi.trendIcon"
            :style="{ color: kpi.trendColor }"
          ></i>
          <span>{{ kpi.sub }}</span>
        </div>
      </div>
    </div>

    <!-- 3. AI 総合分析レポート -->
    <div class="vf-card vf-report-card">
      <div class="vf-card-accent vf-card-accent--ai"></div>
      <div class="vf-report-head">
        <span class="vf-ai-badge">AI</span>
        <h2 class="vf-report-title">総合分析レポート</h2>
      </div>

      <div class="vf-report-summary">
        <div class="vf-report-subhead">
          <span class="vf-report-bar"></span>
          <h3>サマリー</h3>
        </div>
        <p class="vf-report-lead">
          <template v-for="(seg, si) in typingBlocks.summary" :key="si">
            <strong v-if="seg.em" :class="emClass(seg.em)">{{ segText("summary", si) }}</strong>
            <template v-else>{{ segText("summary", si) }}</template>
          </template>
          <span v-if="isTypingNow('summary')" class="vf-type-caret"></span>
        </p>
      </div>

      <div class="vf-report-columns">
        <!-- 分析結果情報 -->
        <div class="vf-report-col">
          <div class="vf-report-subhead">
            <span class="vf-report-bar"></span>
            <h3>分析結果情報</h3>
          </div>
          <div class="vf-report-panel">
            <div class="vf-report-panel-head">
              <i class="fas fa-users"></i>
              <h4>来客傾向分析</h4>
            </div>
            <div class="vf-report-paragraphs">
              <p v-for="pid in ['p1', 'p2']" :key="pid">
                <template v-for="(seg, si) in typingBlocks[pid]" :key="si">
                  <strong v-if="seg.em" :class="emClass(seg.em)">{{ segText(pid, si) }}</strong>
                  <template v-else>{{ segText(pid, si) }}</template>
                </template>
                <span v-if="isTypingNow(pid)" class="vf-type-caret"></span>
              </p>
            </div>
            <div class="vf-report-paragraphs">
              <p v-for="pid in ['p3', 'p4']" :key="pid">
                <template v-for="(seg, si) in typingBlocks[pid]" :key="si">
                  <strong v-if="seg.em" :class="emClass(seg.em)">{{ segText(pid, si) }}</strong>
                  <template v-else>{{ segText(pid, si) }}</template>
                </template>
                <span v-if="isTypingNow(pid)" class="vf-type-caret"></span>
              </p>
            </div>
          </div>
        </div>

        <!-- アクションプラン -->
        <div class="vf-report-col">
          <div class="vf-report-subhead">
            <span class="vf-report-bar"></span>
            <h3>アクションプラン</h3>
          </div>
          <div class="vf-report-panel">
            <div class="vf-report-panel-head vf-report-panel-head--orange">
              <i class="far fa-calendar"></i>
              <h4>短期施策（1-2週間）</h4>
            </div>
            <ol class="vf-action-list">
              <!-- v-show は v-for 要素に patchFlag が付かず更新されない（Vue 3.3 compiler の問題）ため class で制御 -->
              <li
                v-for="(action, i) in shortTermActions"
                :key="action.title"
                :class="{ 'vf-action-hidden': !blockStarted(`a${i}t`) }"
              >
                <span class="vf-action-num">{{ i + 1 }}.</span>
                <div class="vf-action-body">
                  <div class="vf-action-title">
                    {{ segText(`a${i}t`, 0) }}<span v-if="isTypingNow(`a${i}t`)" class="vf-type-caret"></span>
                  </div>
                  <div class="vf-action-desc">
                    {{ segText(`a${i}d`, 0) }}<span v-if="isTypingNow(`a${i}d`)" class="vf-type-caret"></span>
                  </div>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. 予測根拠・影響要因 -->
    <div class="vf-card vf-factors-card">
      <div class="vf-card-accent"></div>
      <h2 class="vf-list-title vf-factors-heading">予測根拠・影響要因</h2>
      <div class="vf-factors-grid">
        <div v-for="factor in aiFactors" :key="factor.title" class="vf-factor-tile">
          <div class="vf-factor-circle">{{ factor.score }}</div>
          <div class="vf-factor-kicker">影響度</div>
          <div class="vf-factor-bar">
            <div class="vf-factor-bar-fill" :style="{ width: `${factor.score}%` }"></div>
          </div>
          <div class="vf-factor-title">{{ factor.title }}</div>
          <div class="vf-factor-desc">{{ factor.description }}</div>
        </div>
      </div>
    </div>
    
    <!-- 3. Composed chart -->
    <div class="vf-card vf-chart-card">
      <div class="vf-card-accent"></div>
      <div class="vf-chart-head">
        <div class="vf-chart-head-left">
          <h2 class="vf-chart-title">日別来客数推移 + 3日先予測</h2>
          <div class="vf-chart-legend">
            <span class="vf-legend-item"><span class="vf-legend-swatch vf-legend-actual"></span>実績</span>
            <span class="vf-legend-item"><span class="vf-legend-swatch vf-legend-forecast"></span>予測</span>
            <span class="vf-legend-item"><span class="vf-legend-swatch vf-legend-occupancy"></span>充足率</span>
          </div>
        </div>
        <div class="vf-chart-disclaimer">
          <i class="fas fa-circle-exclamation"></i>
          予測値は過去データに基づく推定値です
        </div>
      </div>
      <div class="vf-chart-canvas-wrap">
        <canvas v-if="vfChartMounted" ref="vfChartRef"></canvas>
      </div>
    </div>

    

    <!-- 5/6. エリア別・劇場別来客予測（Figma: 2カラム横並び） -->
    <div class="vf-bottom-grid">
      <!-- 5. エリア別来客予測 -->
      <div class="vf-card vf-list-card">
        <div class="vf-card-accent"></div>
        <div class="vf-list-head">
          <h2 class="vf-list-title">
            <i class="fas fa-map-marker-alt"></i>
            エリア別来客予測
          </h2>
          <div class="vf-period-box">
            <span>期間 : 2025/12/01 ~ 2025/12/31</span>
            <i class="far fa-calendar"></i>
          </div>
        </div>
        <div class="vf-row-list">
          <div
            v-for="area in areas"
            :key="area.name"
            class="vf-row vf-row-area"
            :class="`vf-row--${area.tier}`"
          >
            <div class="vf-row-info">
              <div class="vf-row-name">{{ area.name }}</div>
              <div class="vf-row-meta">充足率: {{ area.occupancy }}% | 座席数: {{ area.seats.toLocaleString() }}</div>
            </div>
            <div class="vf-row-values">
              <div class="vf-transition">{{ area.actual }}→{{ area.forecast }}</div>
              <div
                class="vf-row-delta"
                :class="area.deltaPct.startsWith('-') ? 'vf-delta--down' : 'vf-delta--up'"
              >
                {{ area.deltaPct }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 6. 劇場別来客予測 TOP7 -->
      <div class="vf-card vf-list-card">
        <div class="vf-card-accent"></div>
        <div class="vf-list-head">
          <h2 class="vf-list-title">劇場別来客予測 TOP7</h2>
          <div class="vf-period-box">
            <span>期間 : 2025/12/01 ~ 2025/12/31</span>
            <i class="far fa-calendar"></i>
          </div>
        </div>
        <div class="vf-row-list">
          <div
            v-for="t in theatres"
            :key="t.rank"
            class="vf-row vf-row-theatre"
            :class="`vf-row--${t.tier}`"
          >
            <div class="vf-row-left">
              <span class="vf-rank">{{ t.rank }}</span>
              <div class="vf-row-info">
                <div class="vf-row-name">{{ t.name }}</div>
                <div class="vf-row-meta">充足率: {{ t.occupancy }}%</div>
              </div>
            </div>
            <div class="vf-row-values">
              <div class="vf-transition">{{ t.actual }}→{{ t.forecast }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 7. 日別明細 -->
    <div class="vf-card vf-table-card">
      <div class="vf-card-accent"></div>
      <div class="vf-list-head">
        <h2 class="vf-list-title">日別明細</h2>
        <button class="vf-csv-btn" @click="exportCsv">
          <i class="fas fa-download"></i>
          <span>CSV出力</span>
        </button>
      </div>
      <el-table :data="daily" class="vf-table" :row-class-name="dailyRowClass">
        <el-table-column prop="date" label="日付" min-width="210" fixed>
          <template #default="{ row }">
            <span class="vf-td-date">{{ row.date }}</span>
            <span v-if="row.holiday" class="vf-holiday-tag">祝</span>
          </template>
        </el-table-column>
        <el-table-column label="曜日" min-width="118">
          <template #default="{ row }">
            <span :class="{ 'vf-weekend': isWeekend(row.weekday) }">{{ row.weekday }}</span>
          </template>
        </el-table-column>
        <el-table-column label="来客数" min-width="245" align="right">
          <template #default="{ row }">
            <div
              class="vf-visitors-cell"
              :class="{ 'vf-visitors-cell--forecast': row.kind === '予測' }"
            >
              <span class="vf-visitors-num">{{ row.visitors.toLocaleString() }}</span>
              <span v-if="row.kind === '予測' && row.forecastRange" class="vf-visitors-range">
                ({{ row.forecastRange }})
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="総座席数" min-width="180" align="right">
          <template #default="{ row }">{{ row.totalSeats.toLocaleString() }}</template>
        </el-table-column>
        <el-table-column label="充足率" min-width="149" align="right">
          <template #default="{ row }">{{ row.occupancy }}%</template>
        </el-table-column>
        <el-table-column prop="screenings" label="上映回数" min-width="180" align="right" />
        <el-table-column prop="avgPerScreening" label="1回平均" min-width="164" align="right" />
        <el-table-column label="前日比" min-width="169" align="right">
          <template #default="{ row }">
            <span :class="dodClass(row.dod)">{{ dodText(row.dod) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="区分" min-width="143">
          <template #default="{ row }">
            <span class="vf-kind-tag" :class="row.kind === '予測' ? 'vf-kind-forecast' : 'vf-kind-actual'">
              {{ row.kind }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 8. AI チャット（右下フローティング） -->
    <div class="vf-ai-chat">
      <input
        v-model="aiChatInput"
        class="vf-ai-chat-input"
        type="text"
        placeholder="AIエージェントに相談する"
        @keyup.enter="sendAiChat"
      />
      <button class="vf-ai-chat-send" type="button" aria-label="送信" @click="sendAiChat">
        <i class="fas fa-paper-plane"></i>
      </button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.vf-detail-page {
  min-height: 100vh;
  background: var(--bg-body, #f9f9f9);
  padding: 28px 32px 48px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 16px 40px;
  }
}

// ─── Card container ───────────────────────────────────────────
.vf-card {
  background: var(--bg-panel, #ffffff);
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 6px;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1));
  margin-bottom: 20px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

// ─── 1. Header (Figma node 1617-166) ──────────────────────────
.vf-header-card {
  // Figma: padding 25px 25px 1px + 固定高さ 157.39px → 実質の下余白は 25px
  padding: 25px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.vf-header-top {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  // モバイル: 最終更新が収まらない場合は折り返して右寄せのまま2行目へ
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
}

.vf-header-divider {
  width: 1px;
  height: 24px;
  background: var(--border-color, #e5e5e5);
}

.vf-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-secondary, #4a4a4a);
  font-size: 14.4px;
  line-height: 22px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent-orange, #f97316);
  }
}

.vf-header-titles {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.vf-eyebrow {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  background: var(--accent-green, #10b981);
  color: #ffffff;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.vf-title {
  font-size: 24px;
  line-height: 36px;
  font-weight: 700;
  color: var(--text-primary, #000000);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 30px;
  }
}

.vf-last-updated {
  margin-left: auto;
  font-size: 13.6px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.vf-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13.6px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);

  // モバイル: ビュレット区切りの1行表示 → 縦積みリスト
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

.vf-dot {
  color: var(--text-secondary, #4a4a4a);

  @media (max-width: 768px) {
    display: none;
  }
}

.vf-model {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.vf-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent-green, #10b981);
  display: inline-block;
}

// ─── 2. KPI (Figma node 1617-166) ─────────────────────────────
.vf-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 24px;
  margin-bottom: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.vf-kpi-card {
  position: relative;
  background: var(--bg-panel, #ffffff);
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 6px;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1));
  padding: 21px;
  overflow: hidden;
}

// 上端アクセントバー（border の内側に 2px インセット）
.vf-kpi-accent {
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  height: 4px;
}

.vf-kpi-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.vf-kpi-label {
  font-size: 13.6px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);
}

.vf-kpi-icon {
  font-size: 16px;
}

.vf-kpi-value {
  font-size: 28px;
  line-height: 42px;
  font-weight: 700;
  margin-bottom: 4px;
}

.vf-kpi-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.8px;
  line-height: 19px;
  flex-wrap: wrap;
}

.vf-kpi-trend-icon {
  font-size: 12px;
}

// ─── 3. Chart (Figma node 1617-166) ───────────────────────────
.vf-chart-card {
  position: relative;
  padding: 25px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

// 上端アクセントバー（黄→橙グラデーション、2px インセット）— チャート/エリア/劇場カード共通
.vf-card-accent {
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  height: 4px;
  background: linear-gradient(90deg, var(--brand-color, #f9da49) 0%, var(--accent-orange, #f97316) 100%);
}

.vf-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  // モバイル: タイトル＋凡例の下に注記を縦積み
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
}

.vf-chart-head-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.vf-chart-title {
  margin: 0;
  font-size: 14.4px;
  line-height: 22px;
  font-weight: 700;
  color: var(--text-secondary, #4a4a4a);
}

.vf-chart-legend {
  display: flex;
  align-items: center;
  gap: 16px;
}

.vf-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.8px;
  line-height: 19px;
  color: var(--text-secondary, #4a4a4a);
}

.vf-legend-swatch {
  width: 12px;
  height: 2px;

  &.vf-legend-actual {
    background: var(--accent-green, #10b981);
  }
  &.vf-legend-forecast {
    height: 0;
    border-top: 2px dashed #9ca3af;
  }
  &.vf-legend-occupancy {
    background: rgba(0, 102, 255, 0.3);
  }
}

.vf-chart-disclaimer {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.8px;
  line-height: 19px;
  color: var(--text-secondary, #4a4a4a);

  i {
    font-size: 16px;
    color: var(--accent-orange, #f97316);
  }
}

.vf-chart-canvas-wrap {
  position: relative;
  height: 450px;

  @media (max-width: 768px) {
    height: 280px;
  }

  canvas {
    width: 100% !important;
    height: 100% !important;
  }

  // カスタムツールチップ（external handler が生成する素の DOM のため :deep 指定）
  :deep(.vf-chart-tooltip) {
    position: absolute;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 296px;
    max-width: calc(100% - 8px);
    padding: 16px;
    box-sizing: border-box;
    background: var(--bg-panel, #ffffff);
    border: 1px solid var(--border-color, #e5e5e5);
    border-radius: 4px;
    filter: drop-shadow(0 0 13px rgba(0, 0, 0, 0.11));
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;

    .vf-tt-date {
      font-size: 14px;
      line-height: 24px;
      font-weight: 700;
      color: var(--text-secondary, #4a4a4a);
    }

    // Figma: Hiragino Sans / 500 / 14px / lh 24
    // 実績来客数=#23B883 / 予測来客数=#858585 / 充足率=#126BFB
    .vf-tt-values {
      font-family: "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif;
      font-size: 14px;
      line-height: 24px;
      font-weight: 500;

      .vf-tt-line--actual {
        color: #23b883;
      }
      .vf-tt-line--forecast {
        color: #858585;
      }
      .vf-tt-line--occupancy {
        color: #126bfb;
      }
    }

    .vf-tt-ai {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .vf-tt-ai-head {
      display: flex;
      align-items: center;
      gap: 9px;
    }

    .vf-tt-ai-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
      color: #ffffff;
      font-size: 13.2px;
      font-weight: 700;
    }

    .vf-tt-ai-title {
      font-size: 14px;
      line-height: 24px;
      font-weight: 700;
      color: var(--text-secondary, #4a4a4a);
    }

    .vf-tt-ai-body {
      font-size: 12px;
      line-height: 22px;
      color: var(--text-primary, #111111);
    }
  }
}

// ─── 3. AI 総合分析レポート (Figma node 1617-166) ──────────────
// アクセント・バッジ・縦バーは紫→ピンクのグラデーション（#8B5CF6 → #EC4899）
.vf-card-accent--ai {
  background: linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%);
}

.vf-report-card {
  position: relative;
  // Figma: コンテンツ幅 1576 ≒ カード幅 − 25×2、セクション間 gap 21px
  padding: 25px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.vf-report-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 21px;
}

.vf-ai-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
  color: #ffffff;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}

.vf-report-title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: var(--text-secondary, #4a4a4a);
}

.vf-report-summary {
  margin-bottom: 21px;
}

.vf-report-subhead {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 15px;
    line-height: 22px;
    font-weight: 700;
    color: var(--text-primary, #000000);
  }
}

// 見出し横の縦グラデーションバー（4×24 pill）
.vf-report-bar {
  flex-shrink: 0;
  width: 4px;
  height: 24px;
  border-radius: 999px;
  background: linear-gradient(180deg, #8b5cf6 0%, #ec4899 100%);
}

.vf-report-lead {
  margin: 0;
  font-size: 18px;
  line-height: 23px;
  color: var(--text-primary, #111111);
}

// 本文中の強調色（スクリーンショット準拠）
.vf-em-green {
  color: var(--accent-green, #10b981);
}
.vf-em-orange {
  color: var(--accent-orange, #f97316);
}

// タイプライター演出の点滅キャレット
.vf-type-caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: -0.12em;
  background: #8b5cf6;
  animation: vf-caret-blink 0.8s steps(1) infinite;
}

@keyframes vf-caret-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

// レポート出現演出: 読み込み完了後、各セクションを下からふわっとフェードイン（段階表示）。
// タイプライター演出の置き換え。typedTotal は描画前に全文表示済みなので、ここでは純粋に CSS で見せる。
@keyframes vf-report-reveal {
  from {
    opacity: 0;
    transform: translateY(14px);
    filter: blur(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.vf-report-card {
  // サマリー → 分析カラム（分析結果 / アクションプラン）の順にカスケード表示
  .vf-report-summary {
    animation: vf-report-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.05s;
  }
  .vf-report-columns > *:nth-child(1) {
    animation: vf-report-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.2s;
  }
  .vf-report-columns > *:nth-child(2) {
    animation: vf-report-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.32s;
  }
}

// アクションプラン各行も軽く順次表示
.vf-action-list > li {
  animation: vf-report-reveal 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.vf-action-list > li:nth-child(1) {
  animation-delay: 0.42s;
}
.vf-action-list > li:nth-child(2) {
  animation-delay: 0.5s;
}
.vf-action-list > li:nth-child(3) {
  animation-delay: 0.58s;
}
.vf-action-list > li:nth-child(n + 4) {
  animation-delay: 0.66s;
}

// アクセシビリティ: モーション軽減設定時はアニメーションを無効化し即時表示
@media (prefers-reduced-motion: reduce) {
  .vf-report-card .vf-report-summary,
  .vf-report-card .vf-report-columns > *,
  .vf-action-list > li {
    animation: none;
  }
}

.vf-report-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.vf-report-col {
  .vf-report-subhead {
    margin-bottom: 16px;
  }
}

.vf-report-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 17px;
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9fafb));
  border-radius: 8px;
}

.vf-report-panel-head {
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 16px;
    color: var(--text-primary, #000000);
  }

  h4 {
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    font-weight: 700;
    color: var(--text-primary, #000000);
  }

  &--orange {
    i,
    h4 {
      color: var(--accent-orange, #f97316);
    }
  }
}

.vf-report-paragraphs {
  display: flex;
  flex-direction: column;
  gap: 8px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    color: var(--text-secondary, #4a4a4a);

    strong {
      color: var(--text-primary, #111111);
    }

    strong.vf-em-green {
      color: var(--accent-green, #10b981);
    }
    strong.vf-em-orange {
      color: var(--accent-orange, #f97316);
    }
  }
}

.vf-action-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    // タイピングが到達するまで行ごと非表示
    &.vf-action-hidden {
      display: none;
    }
  }
}

.vf-action-num {
  flex-shrink: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 700;
  color: var(--accent-orange, #f97316);
}

.vf-action-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vf-action-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text-primary, #000000);
}

.vf-action-desc {
  font-size: 14px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);
}

// ─── 4. 予測根拠・影響要因 (Figma node 1617-166) ───────────────
.vf-factors-card {
  position: relative;
  padding: 25px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.vf-factors-heading {
  margin-bottom: 16px;
}

.vf-factors-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

// タイル（Figma: 249×218 / bg #F9F9F9 / radius 6px）
.vf-factor-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9f9f9));
  border-radius: 6px;
}

// スコア円（56px / 緑 #10B981 / 白 20px bold）
.vf-factor-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--accent-green, #10b981);
  color: #ffffff;
  font-size: 20px;
  line-height: 30px;
  font-weight: 700;
}

.vf-factor-kicker {
  margin-top: 8px;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0.37px;
  text-transform: uppercase;
  color: var(--text-secondary, #4a4a4a);
}

// プログレスバー（トラック #E5E5E5 / フィル #10B981、幅 = score%）
.vf-factor-bar {
  width: 100%;
  height: 5px;
  margin-top: 4px;
  background: var(--border-color, #e5e5e5);
  border-radius: 3px;
  overflow: hidden;
}

.vf-factor-bar-fill {
  height: 100%;
  background: var(--accent-green, #10b981);
  border-radius: 3px;
}

.vf-factor-title {
  margin-top: 12px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text-primary, #000000);
  text-align: center;
}

.vf-factor-desc {
  align-self: stretch;
  margin-top: 8px;
  font-size: 12px;
  line-height: 20px;
  color: var(--text-secondary, #4a4a4a);
}

// ─── 5/6. Area & Theatre (Figma node 1617-166) ────────────────
.vf-bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;

  .vf-card {
    margin-bottom: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.vf-list-card {
  position: relative;
  padding: 25px;
}

.vf-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.vf-list-title {
  margin: 0;
  font-size: 14.4px;
  line-height: 22px;
  font-weight: 700;
  color: var(--text-secondary, #4a4a4a);

  i {
    font-size: 16px;
    color: var(--text-secondary, #4a4a4a);
    margin-right: 8px;
  }
}

// 期間セレクタ（Figma: 214×33 / border #E1E1E1 / radius 6px）
.vf-period-box {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 33px;
  padding: 0 8px 0 10px;
  border: 1px solid var(--border-color, #e1e1e1);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #4a4a4a);
  white-space: nowrap;

  i {
    font-size: 16px;
    color: var(--text-secondary, #4a4a4a);
  }
}

.vf-row-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vf-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9f9f9));
}

.vf-row-area {
  min-height: 64.5px;
}

.vf-row-theatre {
  min-height: 61px;
}

.vf-row-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.vf-row-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vf-row-name {
  font-size: 13.6px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text-primary, #000000);
}

.vf-row-meta {
  font-size: 11px;
  line-height: 16px;
  font-weight: 400;
  color: var(--text-secondary, #4a4a4a);
}

.vf-row-values {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

// 遷移値は矢印込みで黒一色（Figma: "12,850→14,200" 16px / 700 / #000000）
.vf-transition {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: var(--text-primary, #000000);
  white-space: nowrap;
}

.vf-row-delta {
  font-size: 11px;
  line-height: 16px;
  font-weight: 400;
}

.vf-delta--up {
  color: var(--accent-green, #10b981);
}

.vf-delta--down {
  color: #f91640;
}

// ランク番号（全ティア共通 #4A4A4A）
.vf-rank {
  flex-shrink: 0;
  width: 24px;
  font-size: 18px;
  line-height: 27px;
  font-weight: 700;
  color: var(--text-secondary, #4a4a4a);
}

// ティア配色（border-left はティア行のみ。エリア緑行は 4px、他は 3px）
.vf-row--plain {
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9f9f9));
}

.vf-row--green {
  background: #f0fdf4;
  border-left: 3px solid var(--accent-green, #10b981);

  &.vf-row-area {
    border-left-width: 4px;
  }
}

.vf-row--red {
  background: #fef2f2;
  border-left: 3px solid #f91640;
}

.vf-row--orange {
  background: #fef2f2;
  border-left: 3px solid var(--accent-orange, #f97316);
}

// ─── 7. Table (Figma node 1617-166) ───────────────────────────
.vf-table-card {
  position: relative;
  padding: 25px;
}

// CSV出力（ボーダーレス・アイコン+テキストのみ）
.vf-csv-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-secondary, #4a4a4a);
  font-size: 13.6px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    color: var(--accent-orange, #f97316);
  }
}

.vf-table {
  width: 100%;
  font-size: 13.6px;
  --el-table-border-color: var(--border-color, #e5e5e5);
  --el-table-header-bg-color: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f9f9f9));
  --el-table-header-text-color: var(--text-secondary, #4a4a4a);
  --el-table-text-color: var(--text-primary, #0a0a0a);
  --el-table-bg-color: var(--bg-panel, #ffffff);
  --el-table-tr-bg-color: var(--bg-panel, #ffffff);
  --el-table-row-hover-bg-color: color-mix(in srgb, var(--text-primary) 5%, var(--bg-panel));

  :deep(.el-table__header th) {
    font-size: 13.6px;
    font-weight: 600;
  }

  :deep(td.el-table__cell) {
    padding: 12px 0;
    color: var(--text-primary, #0a0a0a);
  }

  // 予測行の網掛け
  :deep(.vf-forecast-row td.el-table__cell) {
    background: color-mix(in srgb, var(--text-primary) 6%, var(--bg-panel, #f5f5f5));
  }
}

.vf-td-date {
  font-weight: 400;
  color: var(--text-primary, #0a0a0a);
}

// 祝日タグ（橙地・白文字）
.vf-holiday-tag {
  margin-left: 8px;
  font-size: 10px;
  line-height: 15px;
  font-weight: 400;
  color: #ffffff;
  background: var(--accent-orange, #f97316);
  padding: 2px 8px;
  border-radius: 3px;
}

// 土日の曜日は橙太字
.vf-weekend {
  color: var(--accent-orange, #f97316);
  font-weight: 600;
}

.vf-visitors-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
}

.vf-visitors-num {
  font-weight: 700;
  color: var(--text-primary, #000000);
}

.vf-visitors-range {
  font-size: 11px;
  line-height: 16px;
  color: #9ca3af;
}

// 予測行は来客数もグレー
.vf-visitors-cell--forecast {
  .vf-visitors-num {
    color: #9ca3af;
  }
}

// 区分タグ（塗りつぶしバッジ・白文字）
.vf-kind-tag {
  display: inline-block;
  font-size: 11px;
  line-height: 16px;
  font-weight: 400;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 3px;
}

.vf-kind-actual {
  background: var(--accent-green, #10b981);
}

.vf-kind-forecast {
  background: #9ca3af;
}

// ─── Shared delta colors ──────────────────────────────────────
.vf-pos {
  color: var(--accent-green, #10b981);
}
.vf-neg {
  color: var(--accent-orange, #f97316);
}

// ─── 8. AI chat (右下フローティング・Figma 400×74) ─────────────
.vf-ai-chat {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 400px;
  height: 74px;
  padding: 0 16px;
  box-sizing: border-box;
  background: color-mix(in srgb, var(--bg-panel, #ffffff) 90%, transparent);
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: var(--shadow-md, 0 25px 50px -12px rgba(0, 0, 0, 0.25));
  border-radius: 16px;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    right: 16px;
    bottom: 16px;
  }
}

.vf-ai-chat-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 8px 16px;
  box-sizing: border-box;
  background: color-mix(in srgb, var(--text-primary) 3.5%, var(--bg-panel, #f8fafc));
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 14px;
  font-size: 14px;
  line-height: 17px;
  color: var(--text-primary, #0a0a0a);
  outline: none;

  &::placeholder {
    color: var(--text-tertiary, #90a1b9);
  }

  &:focus {
    border-color: var(--brand-color, #f9d749);
  }
}

.vf-ai-chat-send {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: var(--brand-color, #f9d749);
  border-radius: 14px;
  cursor: pointer;
  transition: filter 0.2s ease;

  i {
    font-size: 18px;
    color: #181818;
  }

  &:hover {
    filter: brightness(0.96);
  }
}

/* dark: 色付きティア行の背景をパネル基調の淡色へ */
html.dark .vf-detail-page {
  .vf-row--green {
    background: color-mix(in srgb, var(--accent-green, #10b981) 14%, var(--bg-panel, #20242b));
  }

  .vf-row--red {
    background: color-mix(in srgb, var(--accent-red, #ef4444) 12%, var(--bg-panel, #20242b));
  }

  .vf-row--orange {
    background: color-mix(in srgb, var(--accent-orange, #f97316) 12%, var(--bg-panel, #20242b));
  }
}
</style>
